// What the server has said about when this browser's session ends, on this browser's clock.
//
// Every web sign-in and refresh, and every activity report, answers with two durations:
// `refreshTokenExpiresInSeconds`, until the session ends without input, and
// `sessionMaxExpiresInSeconds`, until its cap, which nothing extends. They are durations so a wrong
// client clock cannot matter. Here they become instants counted from when the request was SENT,
// which can only place them earlier than the server meant, however slow the reply.
//
// - No cap field is a legacy token or a dormant stage. It says nothing about this contract, so the
//   reply adds no deadline; its idle policy is still read (below).
// - Equal durations mean the session now runs to the cap. On its own that is no idle deadline: the
//   server may not be ending sessions for idleness at all. But once an idle deadline is known, a
//   later reply saying so means the server has extended the session as far as it goes, so the idle
//   deadline moves up to the cap. An older reply never moves it.
// - A shorter idle duration shows the server enforces an idle limit. Input extends it, so the later
//   end wins: a reply that was slow to arrive cannot take back an extension another one confirmed.
// - The cap is fixed when the session starts, so the earlier end wins. A cap that moves later by
//   more than a request's latency is not this session's, and the earlier bound holds until a reply
//   to a request sent afterwards settles it.
//
// Each reply also says whether the server ends the session for idleness. Only `sessionIdleEnforced:
// true` says it does. The policy is read apart from the durations, so a reply with none still sets
// it, and the newest reply decides. An idle end belongs to the policy it was given under, so a reply
// under the other policy replaces it rather than merging with it: an idle end at the cap from a
// period off would otherwise outlive enforcement's return, and the tab would not check again before
// the cap. The first reply that says not enforced after one that said enforced records when it was
// sent, so the browser's own limit can count from no earlier than that.
//
// Every tab of a sign-in shares the result in `localStorage`, tied to the sign-in's nonce
// (`session_scope.ts`), so a sibling's confirmed extension counts here and an old sign-in's record
// never extends a later one. Without storage each tab keeps its own.
//
// Imports nothing from the `lib` barrel, for the same reason as `session_scope.ts`.
import { startSessionScope } from "./session_scope";
import { isFiniteNumber, recordLastActivity } from "./idle_sign_out";

export const SESSION_DEADLINES_STORAGE_KEY = "labfront_session_deadlines";

// how far apart two readings of the same cap may land: the upstream half of a request's latency
export const CAP_AGREEMENT_MS = 60 * 1000;

export interface SessionDeadlines {
  nonce: string;
  capEndsAt: number | null;
  // null until the server shows it ends this session for idleness
  idleEndsAt: number | null;
  // this tab's last applied reply: which request it answered, and the clocks when that was sent
  sampleSeq: number;
  observedAt: number;
  // `performance.now()` of this page; meaningless on any other, so never persisted across a reload
  observedAtMono: number | null;
  // a cap disagreed with the known one; a reply to a later request settles it
  revalidateAfterSeq: number | null;
  // whether the server ends this session for idleness, as of the reply whose request was sent at
  // `policyObservedAt`, a wall-clock time (null in a reading from a build that knew no policy)
  idleEnforced: boolean;
  policyObservedAt: number | null;
  // when replies stopped saying enforced, while they still do not; null otherwise
  fallbackSince: number | null;
}

type SessionPolicyReading = Pick<
  SessionDeadlines,
  "idleEndsAt" | "idleEnforced" | "policyObservedAt" | "fallbackSince"
>;

interface SharedSessionDeadlines extends SessionPolicyReading {
  nonce: string;
  capEndsAt: number | null;
}

export interface DeadlineRequest {
  seq: number;
  at: number;
  atMono: number | null;
}

export interface DeadlineObservation {
  deadlines: SessionDeadlines | null;
  // the reply contradicted the known cap: ask the server again
  revalidate: boolean;
}

const isDuration = (value: unknown): value is number =>
  Number.isInteger(value) && (value as number) >= 0;
const isInstantOrNull = (value: unknown): value is number | null =>
  value === null || isFiniteNumber(value);

const monoNow = (): number | null =>
  typeof performance === "undefined" ? null : performance.now();

let lastSeq = 0;

// Taken before a request that will answer with deadlines. The sequence orders this tab's own replies;
// it means nothing to another tab.
export const startDeadlineRequest = (): DeadlineRequest => {
  lastSeq += 1;
  return { seq: lastSeq, at: Date.now(), atMono: monoNow() };
};

// Only `true` is enforced. The schema guards on the way in would let the string "true" through.
export const replyEnforcesIdle = (payload: unknown): boolean =>
  (payload as Record<string, unknown> | null)?.sessionIdleEnforced === true;

const readSample = (payload: unknown): { idleSeconds: number; capSeconds: number } | null => {
  const { refreshTokenExpiresInSeconds: idleSeconds, sessionMaxExpiresInSeconds: capSeconds } =
    (payload as Record<string, unknown> | null) ?? {};
  if (!isDuration(idleSeconds) || !isDuration(capSeconds)) return null;
  // the server never reports an idle end past the cap, so such a reply is not to be trusted
  if (idleSeconds > capSeconds) return null;
  return { idleSeconds, capSeconds };
};

const earlier = (a: number | null, b: number | null): number | null =>
  a === null ? b : b === null ? a : Math.min(a, b);
const later = (a: number | null, b: number | null): number | null =>
  a === null ? b : b === null ? a : Math.max(a, b);
const clampToCap = (idleEndsAt: number | null, capEndsAt: number | null): number | null =>
  idleEndsAt === null ? null : earlier(idleEndsAt, capEndsAt);

// A record written by an older build has no policy, so it reads as not enforced, older than any reply
// that said.
const readPolicy = (record: {
  idleEnforced?: unknown;
  policyObservedAt?: unknown;
  fallbackSince?: unknown;
}): Omit<SessionPolicyReading, "idleEndsAt"> => ({
  idleEnforced: record.idleEnforced === true,
  policyObservedAt: isFiniteNumber(record.policyObservedAt) ? record.policyObservedAt : null,
  fallbackSince: isFiniteNumber(record.fallbackSince) ? record.fallbackSince : null,
});

// This tab's reading and its siblings'. The reply sent later decides the policy; send times on one
// browser's clock compare across tabs, where request numbers do not. A tie means not enforced. The
// switch moment is never put before a switch either side saw, so the browser's own limit after it is
// never cut short: when a sibling heard enforced after this tab's switch, the switch came later still.
const mergeReadings = (
  a: SessionPolicyReading,
  b: SessionPolicyReading | null,
): SessionPolicyReading => {
  if (!b) return a;
  const sentAt = (reading: SessionPolicyReading) => reading.policyObservedAt ?? -Infinity;
  const [newer, older] =
    sentAt(a) > sentAt(b) || (sentAt(a) === sentAt(b) && !a.idleEnforced) ? [a, b] : [b, a];
  const { idleEnforced, policyObservedAt } = newer;
  if (older.idleEnforced === idleEnforced) {
    return {
      idleEndsAt: later(a.idleEndsAt, b.idleEndsAt),
      idleEnforced,
      policyObservedAt,
      fallbackSince: idleEnforced ? null : later(a.fallbackSince, b.fallbackSince),
    };
  }
  const switchedAfterOlder = newer.fallbackSince !== null && newer.fallbackSince > sentAt(older);
  return {
    idleEndsAt: newer.idleEndsAt,
    idleEnforced,
    policyObservedAt,
    fallbackSince: idleEnforced
      ? null
      : switchedAfterOlder
        ? newer.fallbackSince
        : policyObservedAt,
  };
};

const readingOf = (record: SessionPolicyReading): SessionPolicyReading => ({
  idleEndsAt: record.idleEndsAt,
  ...readPolicy(record),
});

const readShared = (nonce: string): SharedSessionDeadlines | null => {
  try {
    const value = window.localStorage.getItem(SESSION_DEADLINES_STORAGE_KEY);
    if (value === null) return null;
    const parsed = JSON.parse(value);
    if (
      parsed?.nonce !== nonce ||
      !isInstantOrNull(parsed.capEndsAt) ||
      !isInstantOrNull(parsed.idleEndsAt)
    ) {
      return null;
    }
    return {
      nonce,
      capEndsAt: parsed.capEndsAt,
      idleEndsAt: parsed.idleEndsAt,
      ...readPolicy(parsed),
    };
  } catch {
    // private-mode storage throws on access, and a hand-edited value may not parse
    return null;
  }
};

const publish = (deadlines: SessionDeadlines, capSettled: boolean): void => {
  const shared = readShared(deadlines.nonce);
  const capEndsAt = capSettled
    ? deadlines.capEndsAt
    : earlier(shared?.capEndsAt ?? null, deadlines.capEndsAt);
  const reading = mergeReadings(readingOf(deadlines), shared);
  const record: SharedSessionDeadlines = {
    nonce: deadlines.nonce,
    capEndsAt,
    ...reading,
    idleEndsAt: clampToCap(reading.idleEndsAt, capEndsAt),
  };
  try {
    window.localStorage.setItem(SESSION_DEADLINES_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // private-mode storage throws on write; each tab then goes by its own readings
  }
};

// Folds a reply into what this tab knows about the sign-in `nonce`, and shares the result. A reply
// with no usable deadline keeps what is known, so a dormant answer cannot discard a valid one, unless
// it changes the policy: then the idle end known under the other one goes, and the cap stays.
export const observeSessionDeadlines = ({
  known,
  nonce,
  payload,
  request,
}: {
  known: SessionDeadlines | null;
  nonce: string;
  payload: unknown;
  request: DeadlineRequest;
}): DeadlineObservation => {
  const base = known?.nonce === nonce ? known : null;
  if (base && request.seq < base.sampleSeq) return { deadlines: base, revalidate: false };

  const idleEnforced = replyEnforcesIdle(payload);
  const knownEnforced = base?.idleEnforced === true;
  const policyChanged = base !== null && knownEnforced !== idleEnforced;
  const policy = {
    idleEnforced,
    policyObservedAt: request.at,
    fallbackSince: idleEnforced ? null : knownEnforced ? request.at : (base?.fallbackSince ?? null),
  };
  const applied = {
    sampleSeq: request.seq,
    observedAt: request.at,
    observedAtMono: request.atMono,
  };

  const sample = readSample(payload);
  if (!sample) {
    if (!base || !policyChanged) return { deadlines: base, revalidate: false };
    const deadlines: SessionDeadlines = { ...base, ...applied, ...policy, idleEndsAt: null };
    publish(deadlines, false);
    return { deadlines, revalidate: false };
  }

  const sampleCapEndsAt = request.at + sample.capSeconds * 1000;
  const capOnlyWithNoIdleKnown =
    sample.idleSeconds === sample.capSeconds && (base?.idleEndsAt ?? null) === null;
  const sampleIdleEndsAt = capOnlyWithNoIdleKnown ? null : request.at + sample.idleSeconds * 1000;

  let capEndsAt = sampleCapEndsAt;
  let revalidateAfterSeq = base?.revalidateAfterSeq ?? null;
  let revalidate = false;
  let capSettled = false;
  if (base?.capEndsAt != null) {
    const disagrees = sampleCapEndsAt > base.capEndsAt + CAP_AGREEMENT_MS;
    if (revalidateAfterSeq !== null && request.seq > revalidateAfterSeq) {
      // sent after the disagreement, so it speaks for the session as it is now
      capEndsAt = disagrees ? sampleCapEndsAt : Math.min(base.capEndsAt, sampleCapEndsAt);
      capSettled = disagrees;
      revalidateAfterSeq = null;
    } else if (disagrees) {
      capEndsAt = base.capEndsAt;
      if (revalidateAfterSeq === null) {
        revalidateAfterSeq = request.seq;
        revalidate = true;
      }
    } else {
      capEndsAt = Math.min(base.capEndsAt, sampleCapEndsAt);
    }
  }

  const knownIdleEndsAt = policyChanged ? null : (base?.idleEndsAt ?? null);
  const deadlines: SessionDeadlines = {
    nonce,
    capEndsAt,
    idleEndsAt: clampToCap(later(knownIdleEndsAt, sampleIdleEndsAt), capEndsAt),
    ...applied,
    revalidateAfterSeq,
    ...policy,
  };
  publish(deadlines, capSettled);
  return { deadlines, revalidate };
};

// The session half of the AUTH_SET a sign-in dispatches. A confirmed sign-in is a new browser session,
// whoever it belongs to, so it starts a new scope and its deadlines start from nothing. Signing in is
// the user's own input too, so the shared input clock restarts with it rather than going on from a
// record an earlier session left behind. Called just before that AUTH_SET, with nothing in between
// that could let other work run under the new scope.
export const startSignedInSession = (
  payload: unknown,
  request: DeadlineRequest,
): { sessionNonce: string; sessionDeadlines: SessionDeadlines | null } => {
  recordLastActivity({ at: Date.now() });
  const scope = startSessionScope();
  const { deadlines } = observeSessionDeadlines({
    known: null,
    nonce: scope.nonce,
    payload,
    request,
  });
  return { sessionNonce: scope.nonce, sessionDeadlines: deadlines };
};

// The deadlines that apply to this tab: its own readings and its siblings', for the sign-in `nonce`
export const readSessionDeadlines = (
  own: SessionDeadlines | null,
  nonce: string,
): ({ capEndsAt: number | null } & SessionPolicyReading) | null => {
  const mine = own?.nonce === nonce ? own : null;
  const shared = readShared(nonce);
  const first = mine ?? shared;
  if (!first) return null;

  const reading = mergeReadings(readingOf(first), mine ? shared : null);
  const capEndsAt = earlier(mine?.capEndsAt ?? null, shared?.capEndsAt ?? null);
  return { capEndsAt, ...reading, idleEndsAt: clampToCap(reading.idleEndsAt, capEndsAt) };
};

// The time to measure deadlines against. The wall clock, unless it has been set back since the last
// reading: the time this page has measured since then counts in full either way, so neither a clock
// change nor a sleep the monotonic clock missed hands the session time it never had.
export const sessionDeadlineNow = (
  own: SessionDeadlines | null,
  nowWall: number = Date.now(),
  nowMono: number | null = monoNow(),
): number => {
  if (!own || own.observedAtMono === null || nowMono === null) return nowWall;
  return Math.max(nowWall, own.observedAt + (nowMono - own.observedAtMono));
};

// The reading persisted with this tab's auth, kept only when it is whole and for the same sign-in. What
// orders replies belongs to the page that took it, like its monotonic clock: request numbers start over
// on a new page, so keeping the old ones would set aside every reply until the count caught up. A cap
// disagreement still being settled is asked about again by the next reply that disagrees. The policy
// is kept with its send time, which means the same on every page of this browser.
export const hydrateSessionDeadlines = (
  value: unknown,
  nonce: string | null | undefined,
): SessionDeadlines | null => {
  const record = value as Partial<SessionDeadlines> | null | undefined;
  if (!nonce || record?.nonce !== nonce) return null;
  if (!isFiniteNumber(record.capEndsAt) || !isInstantOrNull(record.idleEndsAt)) return null;
  if (!isFiniteNumber(record.sampleSeq) || !isFiniteNumber(record.observedAt)) return null;
  return {
    nonce,
    capEndsAt: record.capEndsAt,
    idleEndsAt: record.idleEndsAt,
    sampleSeq: 0,
    observedAt: record.observedAt,
    observedAtMono: null,
    revalidateAfterSeq: null,
    ...readPolicy(record),
  };
};

// Drops the shared record once its sign-in is over, unless a later sign-in already wrote its own
export const clearSharedSessionDeadlines = (nonce: string): void => {
  try {
    if (readShared(nonce)) window.localStorage.removeItem(SESSION_DEADLINES_STORAGE_KEY);
  } catch {
    // a record we cannot remove is one no other tab could have read either
  }
};
